import React from "react";
import { Star, ShieldCheck, Plane } from "lucide-react";
import { useT } from "@/lib/i18n";

const JCTRANS_LOGO =
  "https://customer-assets.emergentagent.com/job_europe-trade-hub/artifacts/yma0nvsx_jctrans_premium_158758.png.png";

export default function TrustBadges() {
  const { t } = useT();
  return (
    <section
      data-testid="trust-badges"
      className="relative py-16 sm:py-20 border-y border-[color:var(--zp-border)] bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="zp-label">{t("badges.eyebrow")}</div>
          <h2 className="font-display text-2xl sm:text-3xl tracking-tight mt-3">
            {t("badges.title.a")}{" "}
            <span className="zp-cyan-text">{t("badges.title.b")}</span>
          </h2>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {/* JCTrans Premium */}
          <a
            data-testid="badge-jctrans"
            href="https://www.jctrans.com/en/member/158758"
            target="_blank"
            rel="noreferrer"
            className="zp-card p-6 flex flex-col items-center justify-between gap-4 text-center group"
          >
            <div className="w-full aspect-[2/1] rounded-xl bg-[color:var(--zp-bg-2)] ring-1 ring-[color:var(--zp-border)] grid place-items-center overflow-hidden">
              <img
                src={JCTRANS_LOGO}
                alt="JCTrans Premium Member ID 158758"
                className="max-h-full w-auto object-contain p-2"
              />
            </div>
            <div>
              <div className="font-display text-base tracking-tight">{t("badges.jctrans.title")}</div>
              <div className="text-xs text-[color:var(--zp-text-3)] mt-1">{t("badges.jctrans.sub")}</div>
            </div>
          </a>

          {/* IATA */}
          <div data-testid="badge-iata" className="zp-card p-6 flex flex-col items-center justify-between gap-4 text-center">
            <div className="w-full aspect-[2/1] rounded-xl bg-gradient-to-br from-[#DCF6F8] to-white ring-1 ring-[color:var(--zp-border-2)] grid place-items-center relative overflow-hidden">
              <div className="absolute inset-0 zp-grid-bg opacity-40" />
              <div className="relative flex items-center gap-2">
                <Plane size={22} className="text-[color:var(--zp-cyan-3)]" strokeWidth={1.9} />
                <span className="font-display text-2xl tracking-[0.18em] font-extrabold text-[color:var(--zp-navy)]">IATA</span>
              </div>
            </div>
            <div>
              <div className="font-display text-base tracking-tight">{t("badges.iata.title")}</div>
              <div className="text-xs text-[color:var(--zp-text-3)] mt-1">{t("badges.iata.sub")}</div>
            </div>
          </div>

          {/* AEO */}
          <div data-testid="badge-aeo" className="zp-card p-6 flex flex-col items-center justify-between gap-4 text-center">
            <div className="w-full aspect-[2/1] rounded-xl bg-gradient-to-br from-[#DCF6F8] to-white ring-1 ring-[color:var(--zp-border-2)] grid place-items-center relative overflow-hidden">
              <div className="absolute inset-0 zp-grid-bg opacity-40" />
              <div className="relative flex flex-col items-center gap-0.5">
                <div className="flex items-center gap-2">
                  <ShieldCheck size={20} className="text-[color:var(--zp-cyan-3)]" strokeWidth={1.9} />
                  <span className="font-display text-2xl tracking-[0.18em] font-extrabold text-[color:var(--zp-navy)]">AEO</span>
                </div>
                <span className="text-[9px] tracking-[0.32em] text-[color:var(--zp-cyan-3)] font-bold">EU CUSTOMS</span>
              </div>
            </div>
            <div>
              <div className="font-display text-base tracking-tight">{t("badges.aeo.title")}</div>
              <div className="text-xs text-[color:var(--zp-text-3)] mt-1">{t("badges.aeo.sub")}</div>
            </div>
          </div>

          {/* Trustpilot-style rating */}
          <div data-testid="badge-trustpilot" className="zp-card p-6 flex flex-col items-center justify-between gap-4 text-center">
            <div className="w-full aspect-[2/1] rounded-xl bg-[color:var(--zp-bg-2)] ring-1 ring-[color:var(--zp-border)] grid place-items-center relative overflow-hidden">
              <div className="relative flex flex-col items-center gap-1.5">
                <div className="flex items-center gap-1">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <span
                      key={i}
                      className="w-7 h-7 rounded-[5px] grid place-items-center bg-[#00B67A] shadow-[0_2px_8px_rgba(0,182,122,0.35)]"
                    >
                      <Star size={14} className="text-white" fill="white" strokeWidth={0} />
                    </span>
                  ))}
                </div>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="font-display text-xl font-bold text-[color:var(--zp-navy)]">4.9</span>
                  <span className="text-xs text-[color:var(--zp-text-2)]">/ 5.0</span>
                </div>
              </div>
            </div>
            <div>
              <div className="font-display text-base tracking-tight">{t("badges.trust.title")}</div>
              <div className="text-xs text-[color:var(--zp-text-3)] mt-1">{t("badges.trust.sub")}</div>
            </div>
          </div>
        </div>

        <p className="text-center text-xs text-[color:var(--zp-text-3)] mt-10 max-w-2xl mx-auto leading-relaxed">
          {t("badges.footer")}
        </p>
      </div>
    </section>
  );
}
